import React, { useContext } from "react";

const ChatHistoryModal = ({ chatHistory, setShowChatHistoryModal, selectedDay, themeContext }) => {
  const isDark =  themeContext === 'dark' ;
  console.log("isDark:", isDark)
  console.log("ChatHistoryModal themeContext:", themeContext);
  const handleCloseModal = () => {
    setShowChatHistoryModal(false);
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Function to detect and extract media URLs from message text
  const extractMediaFromMessage = (message) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const urls = message.match(urlRegex) || [];
    
    const media = {
      videos: [],
      images: [],
      pdfs: [],
      cleanMessage: message
    };

    urls.forEach(url => {
      if (url.match(/\.(mp4|webm|ogg|mov|avi|mkv)(\?.*)?$/i)) {
        media.videos.push(url);
        media.cleanMessage = media.cleanMessage.replace(url, '').trim();
      }
      else if (url.match(/\.(jpg|jpeg|png|gif|webp|svg|bmp)(\?.*)?$/i)) {
        media.images.push(url);
        media.cleanMessage = media.cleanMessage.replace(url, '').trim();
      }
      else if (url.match(/\.pdf(\?.*)?$/i)) {
        media.pdfs.push(url);
        media.cleanMessage = media.cleanMessage.replace(url, '').trim();
      }
    });

    return media;
  };

  // Component to render media content
  const MediaContent = ({ media }) => {
    return (
      <div className="mt-2 space-y-2">
        {/* Render Videos */}
        {media.videos.map((videoUrl, index) => (
          <div key={`video-${index}`} className="rounded-2xl overflow-hidden">
            <video 
              controls 
              className="w-full max-w-sm rounded-2xl"
              preload="metadata"
            >
              <source src={videoUrl} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        ))}

        {/* Render Images */}
        {media.images.map((imageUrl, index) => (
          <div key={`image-${index}`} className="rounded-2xl overflow-hidden">
            <img 
              src={imageUrl} 
              alt={`Shared image ${index + 1}`}
              className="w-full max-w-sm rounded-2xl cursor-pointer hover:opacity-90 transition-opacity"
              onClick={() => window.open(imageUrl, '_blank')}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
        ))}

        {/* Render PDFs */}
        {media.pdfs.map((pdfUrl, index) => (
          <div key={`pdf-${index}`} className={`rounded-2xl overflow-hidden ${
            isDark ? 'bg-[#171A20] border border-[#232733]' : 'bg-gray-50 border border-[#E6E6EA]'
          }`}>
            <div className={`p-3 border-b flex items-center justify-between ${
              isDark ? 'bg-[#1B1F27] border-[#232733]' : 'bg-[#F4F4F6] border-[#E6E6EA]'
            }`}>
              <span className={`text-sm font-medium font-['Poppins'] ${
                isDark ? 'text-[#E7EAF0]' : 'text-[#2E2E2E]'
              }`}>PDF Document</span>
              <button
                onClick={() => window.open(pdfUrl, '_blank')}
                className="text-[#069c27] hover:text-[#05bd42] text-sm underline font-['Poppins'] transition-colors"
              >
                Open in new tab
              </button>
            </div>
            <iframe
              src={pdfUrl}
              className="w-full h-64"
              title={`PDF Document ${index + 1}`}
              frameBorder="0"
            />
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000] px-4 font-['Poppins']">
      <div className={`max-w-xl w-full rounded-2xl relative overflow-hidden shadow-2xl ${
        isDark ? 'bg-[#0F1115]' : 'bg-white'
      }`}>
        {/* Close button */}
        <button
          onClick={handleCloseModal}
          className={`absolute top-4 right-4 text-3xl z-20 rounded-full w-10 h-10 flex items-center justify-center transition-all hover:scale-110 ${
            isDark 
              ? 'text-[#E7EAF0] hover:text-white bg-[#171A20] hover:bg-[#1B1F27] border border-[#232733]' 
              : 'text-[#2E2E2E] hover:text-gray-700 bg-white hover:bg-gray-50 border border-[#E6E6EA]'
          }`}
          aria-label="Close"
        >
          &times;
        </button>

        {/* Header */}
        <div className={`rounded-t-2xl px-6 py-5 border-b ${
          isDark 
            ? 'bg-[#171A20] border-[#232733]' 
            : 'bg-[#F4F4F6] border-[#E6E6EA]'
        }`}>
          <h2 className={`text-lg font-semibold text-center font-['Poppins'] ${
            isDark ? 'text-[#E7EAF0]' : 'text-[#2E2E2E]'
          }`}>
            Your {' '}
            {selectedDay === 0 ? 'Onboarding' : `Day ${selectedDay} `}
            Experience
          </h2>
        </div>

        {/* Scrollable chat content */}
        <div className="mb-6 px-6 py-6 space-y-8 max-h-[75vh] overflow-y-auto custom-scrollbar">
          {chatHistory?.sessions?.length === 0 ? (
            <div className="text-center py-8">
              <div className={`mb-4 ${isDark ? 'text-[#E7EAF0]' : 'text-gray-400'}`}>
                <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <p className={`text-lg font-['Poppins'] ${
                isDark ? 'text-[#E7EAF0]' : 'text-gray-500'
              }`}>No chat history found for this date</p>
            </div>
          ) : (
            chatHistory?.sessions?.map((session, sessionIndex) => (
              <div key={sessionIndex} className="space-y-4">
                {/* Session header with metadata */}
                <div className="flex justify-center mb-4">
                  <span className={`text-xs font-['Poppins'] px-4 py-2 rounded-full ${
                    isDark 
                      ? 'text-[#E7EAF0] bg-[#171A20] border border-[#232733]' 
                      : 'text-[#2E2E2E] bg-[#F4F4F6] border border-[#E6E6EA]'
                  }`}>
                    Session {sessionIndex + 1} • {session.message_count} messages
                  </span>
                </div>
                
                {/* Individual messages in chronological order */}
                <div className="space-y-6">
                  {session.messages?.map((msg, i) => {
                    const media = extractMediaFromMessage(msg.message);
                    
                    return (
                      <div key={i} className="space-y-3">
                        {msg.sender === 'bot' ? (
                          <div className="flex items-start space-x-3">
                            <div className={`w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center ${
                              isDark ? 'bg-[#1B1F27]' : 'bg-[#F4F4F6]'
                            }`}>
                              <img
                                src="/assets/images/bot-avatar.png"
                                alt="Ascendra"
                                className="w-9 h-9 rounded-full"
                              />
                            </div>
                            <div className={`p-4 rounded-2xl rounded-tl-sm max-w-[80%] leading-relaxed ${
                              isDark ? 'bg-[#1B1F27] text-[#E7EAF0]' : 'bg-[#eaeaf8] text-[#2E2E2E]'
                            }`}>
                              {media.cleanMessage && (
                                <div className="mb-2 font-['Poppins'] text-sm">{media.cleanMessage}</div>
                              )}
                              <MediaContent media={media} />
                              <div className={`text-xs mt-2 font-['Poppins'] ${
                                isDark ? 'text-[#E7EAF0] opacity-70' : 'text-gray-500'
                              }`}>
                                {formatTime(msg.timestamp)}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-start space-x-3 justify-end">
                            <div className={`p-4 rounded-2xl rounded-tr-sm max-w-[80%] leading-relaxed ${
                              isDark ? 'bg-[#123A2C] text-[#E7EAF0]' : 'bg-[#CFF4D2] text-[#2E2E2E]'
                            }`}>
                              {media.cleanMessage && (
                                <div className="mb-2 font-['Poppins'] text-sm">{media.cleanMessage}</div>
                              )}
                              <MediaContent media={media} />
                              <div className={`text-xs mt-2 text-right font-['Poppins'] ${
                                isDark ? 'text-[#E7EAF0] opacity-70' : 'text-gray-500'
                              }`}>
                                {formatTime(msg.timestamp)}
                              </div>
                            </div>
                            <div className={`w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center ${
                              isDark ? 'bg-[#123A2C]' : 'bg-[#CFF4D2]'
                            }`}>
                              <img
                                src="/assets/images/default-avatar.png"
                                alt="You"
                                className="w-9 h-9 rounded-full"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                
                {/* Session separator */}
                {sessionIndex < chatHistory?.sessions?.length - 1 && (
                  <div className={`border-t pt-4 ${
                    isDark ? 'border-[#232733]' : 'border-[#E6E6EA]'
                  }`}></div>
                )}
              </div>
            ))
          )}
        </div>
        
        {/* Footer with summary */}
        <div className={`px-6 pb-5 border-t ${
          isDark 
            ? 'bg-[#171A20] border-[#232733]' 
            : 'bg-[#F4F4F6] border-[#E6E6EA]'
        }`}>
        </div>
      </div>

      {/* Custom scrollbar styles */}
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: ${isDark ? '#171A20' : '#F4F4F6'};
          border-radius: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: ${isDark ? '#232733' : '#E6E6EA'};
          border-radius: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: ${isDark ? '#2E8C50' : '#069c27'};
        }
      `}</style>
    </div>
  );
}

export default ChatHistoryModal;